/**
 * Global "pause videos when they leave the viewport" manager.
 *
 * Covers ALL video scenarios in one place: on every scroll/resize it pauses any
 * <video> that has scrolled out of view, regardless of which component rendered
 * it. Uses a scroll listener + getBoundingClientRect (not an IntersectionObserver)
 * so the pause fires deterministically once an element is off-screen — including
 * at the very end of a long page.
 *
 * Resume strategy: we resume EXACTLY the videos this manager paused (tagged with
 * `data-scroll-auto-paused`). This means:
 *   - A video that was playing when it scrolled out resumes when it scrolls back.
 *   - A video that was already paused (manually paused, a non-hovered product-card
 *     hover video, etc.) is never touched, so it won't spuriously start playing.
 *   - Components with their own viewport-scoped observers (PDP carousel, hover
 *     videos) keep working; this just adds page-scroll pause/resume on top.
 */

let initialized = false;
let scheduled = false;

const isInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

  if (rect.width === 0 && rect.height === 0) return false;

  const verticallyVisible = rect.bottom > 0 && rect.top < viewportHeight;
  const horizontallyVisible = rect.right > 0 && rect.left < viewportWidth;

  return verticallyVisible && horizontallyVisible;
};

const updateVideos = () => {
  scheduled = false;

  document.querySelectorAll('video').forEach((video) => {
    if (!isInViewport(video)) {
      if (!video.paused) {
        video.pause();
        video.dataset.scrollAutoPaused = 'true';
      }
      return;
    }

    if (video.dataset.scrollAutoPaused) {
      delete video.dataset.scrollAutoPaused;
      if (!video.dataset.manuallyPaused) {
        video.play().catch(() => {});
      }
    }
  });
};

const onScrollOrResize = () => {
  if (scheduled) return;
  scheduled = true;
  window.requestAnimationFrame(updateVideos);
};

export function initScrollPauseVideos() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });

  updateVideos();
}
